import { identity } from 'lodash';
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useFetchCallback } from '../../hooks';
import { DEFAULT_PAGE_SIZE } from '../reduxToolkitUtils';

import {
    CreateUseQueryHookConfig,
    Option,
    QueryHook,
    QueryObject,
} from './createUseQueryHook.types';

const defaultProcessOnFetchArgs = (queryObj: QueryObject) => [queryObj];
const defaultParams = {};

/**
 * @deprecated consider migrating to ReduxToolkit or RT-Query.
 * Or copy-paste this into your project if needed.
 */
export default function createUseQueryHook<O = Option>({
    selectInitialLoading,
    selectInitialOptions,
    useQueryRequest,
    processOnFetchArgs = defaultProcessOnFetchArgs,
    mapOptions = identity<O[]>,
    params = defaultParams,
}: CreateUseQueryHookConfig<O>): QueryHook<O> {
    return (
        config?: { disabled?: boolean; shouldInit?: boolean } | null,
        extraConfig?: Record<string, any>,
    ) => {
        const initialOptions = useSelector(selectInitialOptions);
        const initialLoading = useSelector(selectInitialLoading);

        const [options, setOptions] = useState<O[]>();

        const onRequest = useQueryRequest();

        const { onFetch, loading, isMountedRef } = useFetchCallback(onRequest);

        const { disabled, shouldInit } = config || {};

        const onSearch = useCallback(
            async (query?: string) => {
                if (disabled) return;
                const onFetchArgs = processOnFetchArgs(
                    {
                        page: 0,
                        size: DEFAULT_PAGE_SIZE,
                        query,
                        ...params,
                    },
                    extraConfig,
                );

                const data = await onFetch(...onFetchArgs);
                if (isMountedRef.current) {
                    setOptions(mapOptions(data) || undefined);
                }
            },
            [disabled, onFetch, extraConfig, isMountedRef],
        );

        useEffect(() => {
            if (shouldInit) onSearch();
        }, [shouldInit, onSearch]);

        return {
            onSearch,
            loading: loading || initialLoading,
            options: options || initialOptions,
        };
    };
}
